import FalconComponentCard from "components/common/FalconComponentCard";
import React, { useCallback, useEffect, useState } from "react";
import { Button, Row, Col, Form, Spinner } from "react-bootstrap";
import PreauthorizationListFilter from "./PreauthorizationListFilter";
import { useForm } from "react-hook-form";
import PreauthorizationListTable from "./PreauthorizationListTable";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { getToday } from "helpers/utils";
import {
  GetAllPreauthorizationList,
  GetPreauthorizationExport,
} from "../config/preauthorizationApi";
import {
  handleResetAll,
  handleResetPreauthorizationFilter,
  handlePreauthorizationFilter,
} from "../config/preauthorizationSlice";
import { handleDownloadXLSX } from "helpers/excel";
import { handleResetPatientSearch } from "features/eliglibility/eliglibility-check/config/eligibilityCheckSlice";
import {
  handlePreauthFormData,
  handleResetClaimAll,
  handleResetCreateClaim,
} from "features/cliams/config/claimSlice";
import { Download } from "react-bootstrap-icons";
import { FiDownloadCloud } from "react-icons/fi";

const PrauthorizationList = () => {
  const dispatch = useDispatch();
  const { preauthorizationList, preauthorizationFilters } = useSelector(
    (state) => state.preauthorization,
  );

  const selectedBranch = JSON.parse(localStorage.getItem("selectedBranch"));
  const [totalRows, setTotalRows] = useState(0);
  const [resetTrigger, setResetTrigger] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isDownloading, setIsDownloading] = useState(false);
  const { allPermissionType } = useSelector((state) => state.role);
  const preauthorizationSetting = allPermissionType?.[0]?.rolemngmt_name?.find(
    (item) => item?.rolemngmttyp_name === "Preauthorization",
  );
  const branchSetting = allPermissionType?.[0]?.rolemngmt_name?.find(
    (item) => item?.rolemngmttyp_name === "Branch",
  );
  const { userData } = useSelector((state) => state.auth);
  const { control, handleSubmit, watch, setValue, reset } = useForm({
    defaultValues: {
      payer: "",
      start_date: getToday(),
      end_date: getToday(),
      insurance: "",
      patient_id: "",
      patient_det: "",
      reference_no_ref: "",
      preauthorization_type: "",
      preauthid: "",
      preauthorization_sub_type: "",
      identity_type: "",
      identity_number: "",
      preauth_status: "",
      pat_det: "",
      second_response: false,
      communication: false,
      advance_preauthorization: false,
    },
  });

  useEffect(() => {
    dispatch(handleResetAll({ check: "reset" }));
    dispatch(handleResetCreateClaim());
    dispatch(handlePreauthFormData({ check: "reset" }));
    dispatch(handleResetPatientSearch());
  }, []);

  const requestBody = (customPage = page, customLimit = rowsPerPage) => ({
    branch_id:
      Number(branchSetting?.list) === 1 && userData?.employee?.syspri_id === 1
        ? preauthorizationFilters?.branch?.branch_id || ""
        : selectedBranch?.value || selectedBranch?.branch_id || 1,
    payer_id: preauthorizationFilters?.payer?.payer_id || "",
    insurance_company_id:
      preauthorizationFilters?.insurance?.insurance_company_id || "",
    claim_type:
      preauthorizationFilters?.preauthorization_type?.claim_types_id || "",
    claimsub_types_name:
      preauthorizationFilters?.preauthorization_sub_type?.claimsub_types_id ||
      "",
    ref_number: preauthorizationFilters?.reference_number || "",
    preauthid: preauthorizationFilters?.preauthid || "",
    frm_dte: preauthorizationFilters?.start_date
      ? moment(preauthorizationFilters?.start_date).format("DD-MM-YYYY")
      : "",
    to_dte: preauthorizationFilters?.end_date
      ? moment(preauthorizationFilters?.end_date).format("DD-MM-YYYY")
      : "",
    ident_typ: preauthorizationFilters?.identity_type?.ident_type_id || "",
    pat_det: preauthorizationFilters?.patient || "",
    preauth_sts:
      preauthorizationFilters?.preauthorization_status?.allstatus_code || "",
    is_secondresponse: preauthorizationFilters?.secondary_response || false,
    is_communication: preauthorizationFilters?.communication || false,
    is_advpreauth: preauthorizationFilters?.advanced_authorization || false,
    start_point: customPage,
    limit: customLimit,
  });

  const fetchPreauthorizationList = async (body = {}) => {
    try {
      const response = await dispatch(
        GetAllPreauthorizationList(body),
      ).unwrap();
      setTotalRows(response?.total_rows || 0);
    } catch (error) {
      // toast.error("Something Went Wrong");
    }
  };

  // ✅ Filter Click Handler — resets to page 0
  const handleCheckListFilter = () => {
    const newPage = 0;
    setPage(newPage); // Reset page
    const body = requestBody(newPage, rowsPerPage);
    fetchPreauthorizationList(body);
  };

  // ✅ Pagination handlers
  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  const handlePerRowsChange = useCallback((newPerPage) => {
    setRowsPerPage(newPerPage);
    setPage(0);
  }, []);

  // ✅ Reset Button Handler
  const handleReset = async () => {
    const defaultBranch = selectedBranch
      ? {
          branch_name: selectedBranch.label || selectedBranch.branch_name,
          branch_id: selectedBranch.value || selectedBranch.branch_id,
          branch_cchino: selectedBranch.cchi || selectedBranch.branch_cchino,
        }
      : null;

    reset({
      branch: defaultBranch,
      payer: "",
      start_date: getToday(),
      end_date: getToday(),
      payer_id: "",
      insurance: "",
      patient_id: "",
      patient_det: "",
      reference_no_ref: "",
      preauthorization_type: "",
      preauthorization_sub_type: "",
      identity_type: "",
      identity_number: "",
      preauth_status: "",
      preauthid: "",
      pat_det: "",
      second_response: false,
      communication: false,
      advance_preauthorization: false,
    });

    setPage(0);
    setRowsPerPage(10);
    setResetTrigger(Math.random());

    dispatch(handleResetPreauthorizationFilter());

    if (defaultBranch) {
      dispatch(
        handlePreauthorizationFilter({ check: "branch", value: defaultBranch }),
      );
    }

    const resetBody = {
      branch_id: defaultBranch?.branch_id || "",
      payer_id: "",
      insurance_company_id: "",
      claim_type: "",
      claimsub_types_name: "",
      ref_number: "",
      frm_dte: moment(getToday()).format("DD-MM-YYYY"),
      to_dte: moment(getToday()).format("DD-MM-YYYY"),
      ident_typ: "",
      pat_det: "",
      preauth_sts: "",
      preauthid: "",
      is_secondresponse: false,
      is_communication: false,
      is_advpreauth: false,
      start_point: 0,
      limit: 10,
    }; // Fresh filters
    await fetchPreauthorizationList(resetBody);
  };

  // ✅ Fetch when page/rowsPerPage change
  useEffect(() => {
    const body = requestBody(page, rowsPerPage);
    fetchPreauthorizationList(body);
  }, [page, rowsPerPage]);

  const handleDownloadExcel = async () => {
    try {
      setIsDownloading(true);
      const baseExcelBody = {
        branch_id:
          Number(branchSetting?.list) === 1 &&
          userData?.employee?.syspri_id === 1
            ? preauthorizationFilters?.branch?.branch_id || ""
            : selectedBranch?.value || selectedBranch?.branch_id || "",
        payer_id: preauthorizationFilters?.payer?.payer_id || "",
        insurance_company_id:
          preauthorizationFilters?.insurance?.insurance_company_id || "",
        claim_type:
          preauthorizationFilters?.preauthorization_type?.claim_types_id || "",
        claimsub_types_name:
          preauthorizationFilters?.preauthorization_sub_type
            ?.claimsub_types_id || "",
        ref_number: preauthorizationFilters?.reference_number || "",
        preauthid: preauthorizationFilters?.preauthid || "",
        frm_dte: preauthorizationFilters?.start_date
          ? moment(preauthorizationFilters?.start_date).format("DD-MM-YYYY")
          : "",
        to_dte: preauthorizationFilters?.end_date
          ? moment(preauthorizationFilters?.end_date).format("DD-MM-YYYY")
          : "",
        ident_typ: preauthorizationFilters?.identity_type?.ident_type_id || "",
        pat_det: preauthorizationFilters?.patient || "",
        preauth_sts:
          preauthorizationFilters?.preauthorization_status?.allstatus_code ||
          "",
        is_secondresponse: preauthorizationFilters?.secondary_response || false,
        is_communication: preauthorizationFilters?.communication || false,
        is_advpreauth: preauthorizationFilters?.advanced_authorization || false,
      };

      const batchSize = 10000;
      let currentStartPoint = 0;
      let allPreauthorizationData = [];
      let hasMore = true;
      let fetchedCount = 0;

      while (hasMore) {
        const excelBody = {
          ...baseExcelBody,
          start_point: currentStartPoint,
          limit: batchSize,
        };

        const data = await dispatch(
          GetAllPreauthorizationList(excelBody),
        ).unwrap();
        const preauthorizationData = data?.data?.preauth_data || [];
        const totalCount =
          data?.total_rows ?? data?.data?.total_rows ?? totalRows ?? 0;

        if (preauthorizationData.length > 0) {
          allPreauthorizationData = [
            ...allPreauthorizationData,
            ...preauthorizationData,
          ];
          fetchedCount += preauthorizationData.length;
        }

        if (
          preauthorizationData.length < batchSize ||
          (totalCount > 0 && fetchedCount >= totalCount)
        ) {
          hasMore = false;
        } else {
          currentStartPoint += 1;
        }
      }

      const flattenedArray =
        allPreauthorizationData &&
        allPreauthorizationData?.length > 0 &&
        allPreauthorizationData.map((row, index) => ({
          "#": index + 1,
          "Request Date": moment(
            row?.is_advpreauth
              ? row?.responsedatetime
              : row.updated_at || row.created_at,
          ).format("DD-MM-YYYY , hh:mm:ss A"),
          "Response Date": row.responsedatetime
            ? moment(row?.updated_at || row.responsedatetime).format(
                "DD-MM-YYYY , hh:mm:ss A",
              )
            : "-" || "-",
          "Payer Name":
            row?.insurance_direct === 0
              ? `${row?.payer_name} - ${row?.insurance_shname}`
              : row?.payer_name || "",
          "Preauth Id": row?.preauth_nwid || row?.preauthfull_id || "",
          "Approval Number": row?.preauthref || "",
          "Approval Type": row?.preauthclmtyp || "",
          "Patient Identifier": row?.patient_identifier || "",
          "Patient Name": `${row?.patient_firstname || ""} ${row?.patient_familyname || ""}`,
          "Patient ID": row?.patient_nationalid || "",
          Class: row?.class || "",
          Gender: row?.patient_gender || "",
          "Marital Status": row?.patient_maritalstatus || "",
          Occupation: row?.patient_occupation || "",
          "Membership No": row?.memberid || "",
          "Policy Number": row?.polcyno || "",
          "Net Amount": row?.net_amount || 0,
          "Approved Amount": row?.apprve_amount || 0,
          Status: row?.pre_auth_status || "",
          "Advanced PreAuth Identifier": row?.is_advpreauth ? "Yes" : "No",
          "Doctor Name": row?.practitioner_name || "",
          Speciality: row?.crteam_spclty || "",
          Remarks: row?.disposition || "",
          "Request BundleId": row?.req_bundleid || "",
          "Response BundleId": row?.resp_bundleid || "",
          "User Name": row?.is_advpreauth
            ? "Advance Preauth"
            : `${row?.employees_firstname || ""} ${row?.employees_familyname || ""}`,
        }));

      handleDownloadXLSX(
        flattenedArray || [],
        `Preauthorization_List_Export_${moment().format("DD-MM-YYYY_hh:mm:ss")}`,
      );
    } catch (error) {
      console.error("Error downloading excel:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Form
      noValidate
      onSubmit={handleSubmit(handleCheckListFilter)}
      className="p-0"
    >
      <FalconComponentCard>
        <FalconComponentCard.Header className="d-flex fixed-header justify-content-between align-items-center">
          <Col xs="auto m-0">
            <h5 className="text-primary mb-0 fs-10">Preauthorization List</h5>
          </Col>
          <Col xs="auto" className="d-flex gap-2">
            <Button size="sm" variant="outline-danger" onClick={handleReset}>
              Reset
            </Button>
            <Button type="submit" size="sm" variant="success">
              Filter
            </Button>
            {Number(preauthorizationSetting?.export) === 1 && (
              <Button
                size="sm"
                variant="warning"
                onClick={handleDownloadExcel}
                disabled={isDownloading}
              >
                {isDownloading ? (
                  <Spinner size="sm" animation="border" className="me-2" />
                ) : (
                  <FiDownloadCloud size={16} className="me-1" />
                )}
                {isDownloading ? "Downloading..." : "Excel"}
              </Button>
            )}
          </Col>
        </FalconComponentCard.Header>

        <FalconComponentCard.Body
          style={{
            marginTop: "40px",
            overflowY: "auto",
            maxHeight: "calc(100vh - 200px)",
          }}
        >
          <PreauthorizationListFilter
            onSubmit={handleCheckListFilter}
            setValue={setValue}
            watch={watch}
            control={control}
            resetTrigger={resetTrigger}
          />
          <PreauthorizationListTable
            page={page}
            rowsPerPage={rowsPerPage}
            handlePageChange={handlePageChange}
            handlePerRowsChange={handlePerRowsChange}
            data={preauthorizationList}
            totalRows={totalRows}
          />
        </FalconComponentCard.Body>
      </FalconComponentCard>
    </Form>
  );
};

export default PrauthorizationList;
